import React, {useEffect, useMemo, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {availableAssaysAction} from '../../redux/toolkit/availableAssaysSlice';
import {chooseTestValidateAction} from '../../redux/toolkit/chooseTestValidateSlice';
import DashboardContainer from '../sideBar/DashboardContainer';
import Message from '../../helper/messages/Message';
import Loading from '../../helper/Loading';
import PageLayout, {Card, PREDICT_STEPS} from '../layout/PageLayout';
import c from './choosetest.module.css';

const ChooseTestContainer = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const filteredSmilesFromNewTest = location.state?.filter(str => str.trim().length).map(str => str.trim());
    const availableAssaysResult = useSelector(state => state?.availableAssaysReducer);
    const {loading} = availableAssaysResult
    const {assays, status, message, status_code, subscription_status} = availableAssaysResult?.availableAssaysResult;
    const [checkboxSmilesValidate, setCheckboxSmilesValidate] = useState([]);
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (location.state) {
            dispatch(availableAssaysAction({'service_type': 'subscribed'}));
        }
    }, [dispatch, location.state]);

    // Groups with their endpoints narrowed to the search term. A group that ends
    // up empty drops out rather than leaving a stranded heading.
    const visibleGroups = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (assays || [])
            .map(g => ({...g, data: (g.data || []).filter(e => !q || e.name.toLowerCase().includes(q))}))
            .filter(g => g.data.length > 0);
    }, [assays, query]);

    const allIdentifiers = useMemo(
        () => (assays || []).flatMap(g => (g.data || []).map(e => e.identifier)),
        [assays]
    );

    const isSelected = (id) => checkboxSmilesValidate.includes(id);

    const toggle = (id) => setCheckboxSmilesValidate(prev =>
        prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);

    const toggleGroup = (group) => {
        const ids = group.data.map(e => e.identifier);
        const allOn = ids.every(isSelected);
        setCheckboxSmilesValidate(prev => allOn
            ? prev.filter(s => !ids.includes(s))
            : [...new Set([...prev, ...ids])]);
    };

    const handleClick = () => {
        if (checkboxSmilesValidate?.length > 0) {
            const chooseTestValidateForm = {
                assays: checkboxSmilesValidate,
                smiles: filteredSmilesFromNewTest,
                platform_id: 'b5c052ca-a48b-4f90-94dd-4feba49353d3',
            }
            dispatch(chooseTestValidateAction(chooseTestValidateForm));
        }
    };

    const selectedCount = checkboxSmilesValidate.length;
    const ready = selectedCount > 0;

    return (
        <DashboardContainer>
            <PageLayout title='Select endpoints'
                        subtitle='Only endpoints you have access to are listed. To enable more, visit Payments.'
                        steps={PREDICT_STEPS} currentStep={1}>
                {
                    (subscription_status && subscription_status === 'ok' && !loading && status === 'success' && assays?.length > 0) &&
                    <>
                        <div className={c.toolbar}>
                            <input type='search' className={c.search} value={query}
                                   onChange={e => setQuery(e.target.value)}
                                   placeholder='Search endpoints...' aria-label='Search endpoints'/>
                            <div className={c.toolbarActions}>
                                <button type='button' className={c.linkButton}
                                        onClick={() => setCheckboxSmilesValidate(allIdentifiers)}>
                                    Select all
                                </button>
                                <button type='button' className={c.linkButton} disabled={!ready}
                                        onClick={() => setCheckboxSmilesValidate([])}>
                                    Clear
                                </button>
                            </div>
                        </div>

                        {visibleGroups.map((group, k) => {
                            const ids = group.data.map(e => e.identifier);
                            const chosen = ids.filter(isSelected).length;
                            return (
                                <Card key={k} padded={false}>
                                    <div className={c.groupHeader}>
                                        <h2 className={c.groupTitle}>
                                            {group.group_name}
                                            <span className={c.groupCount}>{chosen}/{ids.length}</span>
                                        </h2>
                                        <button type='button' className={c.linkButton}
                                                onClick={() => toggleGroup(group)}>
                                            {ids.every(isSelected) ? 'Clear group' : 'Select all'}
                                        </button>
                                    </div>
                                    <ul className={c.endpointGrid}>
                                        {group.data.map((elem, key) => (
                                            <li key={key}>
                                                {/* the whole row is the label, so the click target is the
                                                    endpoint name rather than a 13px checkbox */}
                                                <label className={c.endpoint}>
                                                    <input type='checkbox'
                                                           value={elem.identifier}
                                                           checked={isSelected(elem.identifier)}
                                                           onChange={() => toggle(elem.identifier)}/>
                                                    <span>{elem.name}</span>
                                                </label>
                                            </li>
                                        ))}
                                    </ul>
                                </Card>
                            );
                        })}

                        {visibleGroups.length === 0 &&
                            <p className={c.noMatches}>No endpoints match “{query}”.</p>}

                        <div className={c.footer}>
                            <span className={c.selectedCount}>
                                {selectedCount} endpoint{selectedCount === 1 ? '' : 's'} selected
                            </span>
                            {ready
                                ? <Link to='/validationResult'>
                                    <button className={c.nextButton} onClick={handleClick}>Next</button>
                                </Link>
                                : <button className={c.nextButton} disabled>Next</button>}
                        </div>
                    </>
                }
                {
                    ((!loading && status !== 'error' && !assays?.length)
                        || (!loading && status !== 'error' && subscription_status && subscription_status !== 'ok'))
                    && <Message message={message}/>
                }
                {status === 'error' && status_code !== 200 && !loading &&
                    <Message message={message} status={status} status_code={status_code}/>
                }
                {
                    loading && <Loading/>
                }
            </PageLayout>
        </DashboardContainer>
    );
};

export default ChooseTestContainer;
